/**
 * The plane API, as the pages see it.
 *
 * Two implementations of the same surface: {@link httpApi} (plane-api.js) calls the backend, the
 * demo backend (../demo/demo-api.js) answers from memory and simulates the clawlets. main.js
 * picks one from the configuration (`api.backend`) before the first page renders; pages import
 * `api` from here and never know which one they got.
 *
 * Shapes, as both implementations return them:
 *
 * - session: `{ username, admin, operator, customers }`: an operator sees every customer, server
 *   and instance; anybody else only the instances of `customers`, and no server (403)
 * - customer: `{ id, name, instances, users }`, names only; for operators
 * - server: `{ id, name, status, connection, connectedSince, disconnectedAt, helloAt,
 *   clawletVersion, imageVersions, desiredGeneration, ackedGeneration, instanceCount,
 *   certificateExpiresAt, enrollmentTokenExpiresAt, lastError, lastErrorAt, lastAddress,
 *   expectedNetworks, clawletGeneration, aheadGeneration, holdReason, unknownInstances,
 *   capabilities }`, with `capabilities` the features of the link its clawlet declared in its
 *   last hello (e.g. 'channels.status'), and `status`
 *   'active' | 'revoked' and `connection` 'online' | 'offline' | 'never'; the two expiries are
 *   null when there is no certificate signed by the plane, or no unused enrollment token;
 *   `lastError` is the last error message the clawlet sent since its hello, null if none;
 *   `lastAddress` the address its last connection came from (null if none), `expectedNetworks`
 *   the CIDR blocks or addresses it is expected to connect from; `clawletGeneration` the
 *   generation of its last hello (null before one), `aheadGeneration` set while the server is
 *   held (its clawlet is ahead of the plane, nothing is sent to it until an operator takes it over,
 *   `takeOverServer`), null otherwise; `holdReason` why it is held, 'ahead' (its hello declared a
 *   generation the plane never sent), 'generation_regression' or 'generation_conflict' (its clawlet
 *   refused a desired state), null when it is not held; `unknownInstances` the instances the clawlet reports that are
 *   not assigned to the server
 * - enrollment token: `{ serverId, token, expiresAt }`, the answer of `createServer` and
 *   `newEnrollmentToken`: the token is shown once, the plane keeps only its hash
 * - enrollment request: `{ id, fingerprint, requestedServerId, hostname, sourceAddress, network,
 *   server, serverLastAddress, createdAt, lastSeenAt }`, a clawlet that asked for a certificate
 *   with the bootstrap key and waits for an operator; `requestedServerId` and `hostname` are its
 *   own word (null if not given), `sourceAddress` what the plane saw, `network` 'expected' |
 *   'local' | 'unknown'; `server` (`{ id, name, status }`, null if none) the server of the id
 *   it asked for, which approving gives a new key
 * - instance (a workspace, in the UI): `{ name, customer, serverId, imageVersion, state, error,
 *   health, observedState, since, desiredRevision, appliedRevision, desiredAckedAt, restartNonce,
 *   ref, canManage, id, displayName, assistantNames }`; `name` is the name of the gateway, null for
 *   a customer user; `displayName` the name of the workspace; `assistantNames` the names of the
 *   assistants of the desired revision, by id (null without one); `canManage` whether the viewer may change its configuration (an
 *   operator, an owner of its customer, not a member); `ref` names the instance in URLs: its name,
 *   or `name@serverId` when another server has an instance of that name, and its `id` for a
 *   customer user; `state` is the
 *   displayed state the backend derives (InstanceStatus): 'running' | 'running_degraded' |
 *   'updating' | 'failed' | 'rollback_failed' | 'stuck' | 'stopped' | 'server_offline' | 'unknown'; the two
 *   revisions are `{ id, number }` or null
 * - instance detail: an instance plus `revisions` (newest first, without content), `secrets`
 *   (`{ name, set, updatedAt }`, never a value), `events`, `pairingRequests`: `{ assistant,
 *   channel, senderId, code, name, username, requestedAt }`, who wrote to an assistant that
 *   accepts requests (`telegram.acceptRequests`) and waits, for those who manage the workspace
 *   (empty for a member), with `channel` 'telegram' | 'whatsapp' and `senderId` a Telegram user id
 *   or a WhatsApp number; `allowPairingRequest` adds the sender to the allowed users of that
 *   channel in a new revision applied at once, `ignorePairingRequest` hides the request until it
 *   expires; and `channels`: `{ assistant, channel, state, error, reportedAt }`, the state of each
 *   channel account ('connected' | 'disconnected' | 'unlinked'), or null when the server does not
 *   report channels and nothing is known
 * - revision: `{ id, number, status, digest, author, createdAt, params, personas, operatorRules }`,
 *   `status` 'draft' | 'published', `personas` the persona `{ soul, agents, user }` of each
 *   assistant by id (the ids of `params.assistants`), as written (`agents` without the operator
 *   rules, which the plane puts at the head of every AGENTS.md); `operatorRules` empty for
 *   none: an operator changes them (`saveRevision` with `operatorRules`, left out to keep those of
 *   the desired revision), a customer reads them
 * - event: `{ id, instance, revisionId, revisionNumber, phase, status, message, at }`, for
 *   operators only: a customer user gets 403, and no events in the detail of a workspace
 * - template: one assistant, `{ id, name, description, imageVersion, assistantId, version,
 *   archived, assistants, behind, updatedBy, updatedAt }` in lists (`assistantId` the id it
 *   proposes for the assistant it creates), plus `params` (of the assistant, without `assistants`
 *   around them), `persona`, `operatorRules` and `copies` (`{ instance, assistant, customer,
 *   templateVersion }`, one per assistant of a workspace made from it; `templateVersion` 0 for an
 *   assistant whose model or thinking, or the operator rules of the workspace it was created from,
 *   an operator changed by hand) alone; for operators. `version` grows at every change,
 *   `assistants` counts the assistants made from it, `behind` those made from an older version
 *   (`updateTemplateInstances` brings them to the current one)
 * - image version: `{ version, paramsSchema }`, a JSON Schema of the exposed parameters; the
 *   properties of an assistant with `"x-audience": "operator"` only an operator may change
 *
 * A customer user gets the same shapes without the internals: `serverId`, `error`,
 * `observedState`, `desiredAckedAt` and `restartNonce` of an instance, `digest` of a revision and
 * `message` of an event are null. A revision of theirs that changes an operator's parameter or the
 * operator rules, or a rollback to one, is refused (403).
 */

/** A failed call: the HTTP status and the code the backend classified the error with. */
export class ApiError extends Error {
  constructor(status, code, message) {
    super(message ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** The implementation in use: set once by main.js, read by every page. */
export let api = null;

export function useApi(implementation) {
  api = implementation;
}
